type CacheKeyParams<TVariables> = {
	url: string | ((variables: TVariables) => string);
	method?: string | ((variables: TVariables) => string);
	headers?: HeadersInit;
	body?: BodyInit | ((variables: TVariables) => BodyInit);
	credentials?: RequestCredentials;
	variables?: TVariables;
};

export function generateCacheKey<TVariables = any>(params: CacheKeyParams<TVariables>): string {
	const { url, method = 'GET', headers, body, credentials, variables } = params;

	return canonicalStringify({
		url: typeof url === 'function' ? url.toString() : url,
		method: typeof method === 'function' ? method.toString() : method,
		headers,
		body: typeof body === 'function' ? body.toString() : body,
		credentials,
		variables,
	});
}

export function generateCacheKeyForMultipleFetches<TVariables = any>(
	requestConfigs: CacheKeyParams<TVariables>[],
	variables?: TVariables,
): string {
	const keys = requestConfigs.map((config) => generateCacheKey({ ...config, variables })).sort();

	return canonicalStringify(keys);
}

// Serializes with sorted object keys for deterministic output
function canonicalStringify(value: any): string {
	return JSON.stringify(value, canonicalStringifyReplacer);
}

function canonicalStringifyReplacer(_key: string, value: any): any {
	// Ensures we only process plain objects (not arrays, dates, etc.)
	if (value && typeof value === 'object') {
		const proto = Object.getPrototypeOf(value);
		if (proto === Object.prototype || proto === null) {
			const keys = Object.keys(value);

			// If keys are already sorted, return the original value unchanged to avoid unnecessary object creation
			if (keys.every(everyKeyInOrder)) {
				return value;
			}

			keys.sort();
			const sortedObject: any = Object.create(proto);

			keys.forEach((key) => {
				sortedObject[key] = value[key];
			});
			return sortedObject;
		}
	}
	return value;
}

function everyKeyInOrder(key: string, i: number, keys: string[]): boolean {
	return i === 0 || keys[i - 1] <= key;
}
